import { useCallback, useEffect, useState } from "react";
import { IndianRupee, Wallet, Receipt, TrendingUp } from "lucide-react";
import PosShell from "@/components/pos/PosShell";
import { formatINR, posApi } from "@/lib/pos";
import { useAuth } from "@/context/AuthContext";

export default function PosDashboard() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    const s = await posApi.todaySummary(token);
    setSummary(s);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const cards = [
    {
      label: "Total sales",
      value: formatINR(summary?.total_sales || 0),
      icon: IndianRupee,
      accent: "#15803d",
      testid: "metric-total-sales",
    },
    {
      label: "Cash received",
      value: formatINR(summary?.cash_received || 0),
      icon: Wallet,
      accent: "#0e6ea6",
      testid: "metric-cash-received",
    },
    {
      label: "Transactions",
      value: String(summary?.count || 0),
      icon: Receipt,
      accent: "#cb05a5",
      testid: "metric-transaction-count",
    },
  ];

  return (
    <PosShell todayTotal={summary ? summary.total_sales : null}>
      <div
        data-testid="pos-dashboard"
        className="mx-auto max-w-5xl px-4 sm:px-6 py-5 space-y-4"
      >
        <div>
          <h1 className="font-black text-2xl text-slate-900">Today's sales</h1>
          <p className="text-sm font-semibold text-slate-500 mt-1">
            {summary?.date || "—"} · counter summary
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {cards.map((c) => (
            <div
              key={c.label}
              data-testid={c.testid}
              className="rounded-[4px] bg-white border-2 border-slate-200 p-5"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
                  {c.label}
                </span>
                <c.icon size={18} style={{ color: c.accent }} strokeWidth={2.6} />
              </div>
              <div
                className="mt-3 font-black text-4xl leading-none tabular-nums text-slate-900"
              >
                {c.value}
              </div>
              <div
                className="mt-4 h-1.5 rounded-full"
                style={{ background: c.accent, opacity: 0.85 }}
              />
            </div>
          ))}
        </div>

        <div className="rounded-[4px] bg-white border-2 border-slate-200 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[4px] bg-slate-900 text-white flex items-center justify-center">
              <TrendingUp size={18} strokeWidth={2.6} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
                Average bill
              </div>
              <div className="font-black text-2xl text-slate-900 tabular-nums leading-none mt-1">
                {formatINR(summary?.avg_bill || 0)}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Change given
            </div>
            <div className="font-black text-2xl text-slate-900 tabular-nums leading-none mt-1">
              {formatINR(summary?.change_given || 0)}
            </div>
          </div>
        </div>
      </div>
    </PosShell>
  );
}
